import { useState, useEffect } from 'react'
import { Globe, TrendingUp, Building2, ShieldCheck, ArrowRight, X, Lock, ExternalLink } from 'lucide-react'
import { companiesApi, accessRequestsApi, documentsApi } from '../lib/api'
import { calculateScore, getScoreColor, getScoreLabel, CATEGORY_LABELS } from '../lib/governanceScore'
import { useToast } from '../lib/ToastContext'
import AccessRequestModal from '../components/AccessRequestModal'
import { SkeletonCard } from '../components/ui/Skeleton'

function countryFlag(country) {
  const flags = {
    Nigeria: 'NG', 'United States': 'US', 'United Kingdom': 'GB', Australia: 'AU',
    Canada: 'CA', Germany: 'DE', Singapore: 'SG', 'South Africa': 'ZA',
    Kenya: 'KE', India: 'IN', UAE: 'AE', Other: 'XX',
  }
  return flags[country] || 'XX'
}

export default function PublicCompanyProfile({ slug }) {
  const [company, setCompany] = useState(null)
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAccessModal, setShowAccessModal] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    let mounted = true
    ;(async () => {
      setLoading(true)
      const { data, error: err } = await companiesApi.getBySlug(slug)
      if (!mounted) return
      if (err) {
        setError(err)
        setLoading(false)
        return
      }
      if (!data) {
        setError('Company not found or not publicly listed.')
        setLoading(false)
        return
      }
      setCompany(data)
      if (data.id) {
        const { data: docData } = await documentsApi.list(data.id)
        if (!mounted) return
        setDocs(docData || [])
      }
      setLoading(false)
    })()
    return () => { mounted = false }
  }, [slug])

  useEffect(() => {
    if (company?.name) {
      document.title = `${company.name} - Bizos Suite`
      const metaDesc = document.querySelector('meta[name="description"]')
      if (metaDesc) {
        metaDesc.setAttribute('content', `${company.name} is a ${company.industry || 'Technology'} company with a validated Governance Health Score of ${company.governance_score || 0}%.`)
      } else {
        const meta = document.createElement('meta')
        meta.name = 'description'
        meta.content = `${company.name} is a ${company.industry || 'Technology'} company with a validated Governance Health Score of ${company.governance_score || 0}%.`
        document.head.appendChild(meta)
      }
      const ogTitle = document.querySelector('meta[property="og:title"]')
      if (!ogTitle) {
        const og = document.createElement('meta')
        og.setAttribute('property', 'og:title')
        og.content = `${company.name} - Bizos Suite`
        document.head.appendChild(og)
      }
      const ogDesc = document.querySelector('meta[property="og:description"]')
      if (!ogDesc) {
        const og = document.createElement('meta')
        og.setAttribute('property', 'og:description')
        og.content = `${company.name} is a ${company.industry || 'Technology'} company with a validated Governance Health Score of ${company.governance_score || 0}%.`
        document.head.appendChild(og)
      }
    }
    return () => { document.title = 'Bizos Suite' }
  }, [company])

  const handleAccessRequest = async (payload) => {
    if (!company?.id) return
    const { error: err } = await accessRequestsApi.create({
      company_id: company.id,
      investor_name: payload.name,
      investor_fund: payload.fund,
      investor_email: payload.email,
      investor_role: payload.role,
      stage_focus: payload.stageFocus || [],
      check_size: payload.checkSize,
    })
    if (err) {
      toast('Failed to submit access request: ' + err, 'error')
    } else {
      toast('Access request submitted successfully.', 'success')
    }
    setShowAccessModal(false)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-bg p-8 lg:p-12 max-w-4xl mx-auto space-y-6">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    )
  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-8">
        <div className="surface p-8 text-center max-w-md">
          <Building2 size={32} className="text-neutral-300 mx-auto mb-4" />
          <h1 className="text-lg font-semibold text-ink mb-2">Company Not Found</h1>
          <p className="text-sm text-muted">{error || 'This company profile is not publicly available.'}</p>
        </div>
      </div>
    )
  }

  const score = calculateScore(docs)
  const hc = getScoreColor(score.overall)
  const scoreLabel = getScoreLabel(score.overall)
  const isPublic = company.public_visibility !== false

  return (
    <div className="min-h-screen bg-bg">
      {/* Header */}
      <header className="border-b border-neutral-200 bg-surface">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-brand flex items-center justify-center">
              <span className="text-white text-xs font-bold font-mono">B</span>
            </div>
            <span className="text-sm font-semibold text-ink">Bizos Suite</span>
          </div>
          <a href="/" className="text-xs text-muted hover:text-brand transition-colors flex items-center gap-1">
            Back to Home <ExternalLink size={12} />
          </a>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-10 space-y-8">
        {/* Company Header */}
        <div className="surface p-8">
          <div className="flex items-start justify-between gap-6">
            <div>
              <h1 className="text-2xl font-bold text-ink">{company.name}</h1>
              <div className="flex items-center gap-3 mt-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold border border-neutral-200 bg-neutral-50 text-neutral-600">
                  <Globe size={12} /> {company.country || 'Unknown'}
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold border border-neutral-200 bg-neutral-50 text-neutral-600">
                  <Building2 size={12} /> {company.industry || 'Technology'}
                </span>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="text-3xl font-bold font-mono text-ink">{score.overall}%</p>
              <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border ${hc.text} border-current`}>
                {scoreLabel}
              </span>
            </div>
          </div>

          {/* Governance Score Bar */}
          <div className="mt-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-neutral-500 mb-2">Governance Health Score</p>
            <div className="w-full h-2.5 bg-neutral-100">
              <div className={`h-full transition-all duration-500 ${hc.bar}`} style={{ width: `${score.overall}%` }} />
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-6">
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
              const val = score[key] || 0
              const cc = getScoreColor(val)
              return (
                <div key={key}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-500">{label}</span>
                    <span className={`text-xs font-mono font-semibold ${cc.text}`}>{val}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-neutral-100">
                    <div className={`h-full ${cc.bar}`} style={{ width: `${val}%` }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Protected Data Notice */}
        <div className="border border-neutral-200 bg-neutral-50 px-6 py-4 flex items-start gap-3">
          <Lock size={18} className="text-neutral-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-ink">Documents are access-controlled</p>
            <p className="text-xs text-muted mt-1">
              Financial records, compliance documents, and internal data are protected behind an access gate.
              Request vault access to view verified documents.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="surface p-6 text-center">
          {isPublic ? (
            <>
              <p className="text-sm text-ink mb-4">Interested in exploring this company's verified governance documents?</p>
              <button
                onClick={() => setShowAccessModal(true)}
                className="btn-primary text-sm flex items-center gap-1.5 mx-auto"
              >
                <ShieldCheck size={15} /> Request Vault Access <ArrowRight size={14} />
              </button>
            </>
          ) : (
            <>
              <Lock size={20} className="text-neutral-400 mx-auto mb-3" />
              <p className="text-sm font-semibold text-ink">Not accepting investor inquiries</p>
              <p className="text-xs text-muted mt-1">This company has set their profile to private.</p>
            </>
          )}
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-muted text-center px-4">
          Businesses listed on this platform are not vetted or endorsed by Bizos Suite. All investment decisions are solely at your own risk.
        </p>
      </div>

      {/* Access Request Modal */}
      <AccessRequestModal
        open={showAccessModal}
        onClose={() => setShowAccessModal(false)}
        companyName={company.name}
        onSubmit={handleAccessRequest}
      />
    </div>
  )
}
